import { compareDomainConfigs } from './centralModel';
import { normaliseFields } from './optionUtils';

const same = (a, b) => compareDomainConfigs(a, b, { ignore: [] }).length === 0;
const object = value => value != null && typeof value === 'object' && !Array.isArray(value);

// Array position owns field order. Do not mistake the editor's combined field
// numbering for a change to a follow-up's separately numbered stored order.
function schema(value) {
  const fields = list => normaliseFields(list || []).map(field => {
    const result = { ...field };
    delete result.order;
    return result;
  });
  return { primary: fields(value?.primary ? [value.primary] : [])[0], followUp: fields(value?.followUp) };
}

/** Rebase only the administrator's edits; retain unrelated saved changes. */
export function mergeDomainFieldChanges(base, local, saved) {
  const conflicts = [];
  const merge = (before, mine, current, path = []) => {
    if (same(mine, before)) return current;
    if (same(current, before) || same(mine, current)) return mine;
    if ([before, mine, current].every(object)
      && [mine, current].every(value => value.id === before.id && value.type === before.type)) {
      return Object.fromEntries([...new Set([...Object.keys(before), ...Object.keys(mine), ...Object.keys(current)])]
        .map(key => [key, merge(before[key], mine[key], current[key], [...path, key])])
        .filter(([, value]) => value !== undefined));
    }
    if ([before, mine, current].every(Array.isArray)) {
      const all = [...before, ...mine, ...current];
      const key = ['id', 'value'].find(candidate => all.length && all.every(item => object(item) && typeof item[candidate] === 'string')
        && [before, mine, current].every(items => new Set(items.map(item => item[candidate])).size === items.length));
      if (key) {
        const ids = items => items.map(item => item[key]);
        const order = same(ids(mine), ids(before)) ? ids(current)
          : same(ids(current), ids(before)) || same(ids(mine), ids(current)) ? ids(mine) : null;
        if (order) {
          const byId = items => new Map(items.map(item => [item[key], item]));
          const [b, l, r] = [before, mine, current].map(byId);
          // Include removed keys in comparison to detect removal versus edit.
          const merged = new Map([...new Set([...b.keys(), ...l.keys(), ...r.keys()])]
            .map(id => [id, merge(b.get(id), l.get(id), r.get(id), [...path, id])]));
          const retained = [...order, ...[...merged.keys()].filter(id => !order.includes(id))];
          return retained.map(id => merged.get(id)).filter(value => value !== undefined);
        }
      }
    }
    conflicts.push({ path, before, local: mine, saved: current });
    return mine;
  };
  const merged = merge(schema(base), schema(local), schema(saved));
  return { merged, conflicts };
}
